import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  FormControlLabel,
  Checkbox,
  Alert,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

import postureImage from "../../utilities/dummy_assets/image24.png";
import sizeImage from "../../utilities/dummy_assets/image25.png";
import BackgroundImage from "../../assets/background.png";
import Logo1 from "../../utilities/dummy_assets/MBF_Logo.png";
import mapITlogo from "../../utilities/dummy_assets/svgs/MapIt_Logo.svg";
import { toast } from "react-toastify";
import PageTransition from "components/PageTransition/PageTransition";

/**
 * RegisterInstructions Component
 * Displays profile photo guidelines before camera capture
 * Shows visual examples of proper posture and framing for profile pictures
 * Provides navigation to camera capture or back to login
 */
const RegisterInstructions = () => {
  const navigate = useNavigate();

  const [isChecked, setIsChecked] = useState(false);
  const [showError, setShowError] = useState(false);

  // Handle "Open Camera" button click
  const handleOpenCamera = async () => {
    if (!isChecked) {
      setShowError(true);
      return;
    }
    setShowError(false);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      // Stop camera after permission check so it doesn’t stay open on this page
      stream.getTracks().forEach((track) => track.stop());
      navigate("/profile-picture");
    } catch (err) {
      toast.error(
        "Camera access denied. Please allow camera permission to proceed."
      );
    }
  };

  return (
    <Box
      sx={{
        width: "100vw",
        height: "100vh",
        overflow: "hidden",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: { xs: "#FFFFFF", sm: "#F7F7F7" },
        backgroundImage: { xs: "none", sm: `url(${BackgroundImage})` },
        backgroundSize: "cover",
        backgroundPosition: "center",
        position: "relative",
        padding: { xs: "24px 8px 32px 8px", sm: "24px" },
        boxSizing: "border-box",
        flexDirection: "column",
      }}
    >
      {/* Top Left Logo */}
      <Box sx={{ position: "absolute", top: { xs: 10, sm: 20 }, left: { xs: 10, sm: 20 } }}>
        <img
          src={Logo1}
          alt="MBF Logo"
          style={{ height: "auto", width: "auto", maxHeight: "50px" }}
        />
      </Box>

      {/* Top Right Link */}
      <Box sx={{ position: "absolute", top: { xs: 10, sm: 20 }, right: { xs: 10, sm: 20 } }}>
        <Typography
          component="a"
          href={process.env.REACT_APP_ADMIN_SITE_BASE_URL}
          sx={{
            textDecoration: "underline",
            color: "#007BFF",
            fontWeight: 500,
            fontSize: { xs: "14px", sm: "16px" },
          }}
        >
          Go to Admin site
        </Typography>
      </Box>

      {/* Main Card */}
      <PageTransition
        sx={{
          width: "100%",
          maxWidth: { xs: "520px", sm: "460px" },
          backgroundColor: "#FFFFFF",
          borderRadius: { xs: "0px", sm: "24px" },
          padding: { xs: "0.5rem", sm: "0.75rem" },
          boxShadow: { xs: "none", sm: "4px 4px 20px rgba(0, 123, 255, 0.1)" },
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: { xs: "6px", sm: "8px" },
          overflowY: "auto",
          maxHeight: { xs: "calc(100vh - 120px)", sm: "calc(100vh - 140px)" },
        }}
      >
        {/* Title */}
        <Typography
          variant="h6"
          fontWeight={600}
          color="#212121"
          align="center"
          sx={{ mb: { xs: 0, sm: 0.5 }, fontSize: { xs: "18px", sm: "20px" } }}
        >
          Set Your Profile Photo
        </Typography>
        {/* Note */}
        <Box
          sx={{
            width: "100%",
            backgroundColor: "#FEFFD2",
            borderLeft: "4px solid #D0A300",
            borderRadius: "4px",
            padding: "10px 12px",
            textAlign: "center",
          }}
        >
          <Typography fontSize="14px" fontWeight={500} color="#D0A300">
            Keep these things in mind while clicking your picture
          </Typography>
        </Box>

        {/* Posture Section */}
        <Box sx={{ width: "100%" }}>
          <Typography fontWeight={500} fontSize={{ xs: "14px", sm: "16px" }} color="#3F3F3F" mb={0.5}>
            Posture:
          </Typography>
          <img
            src={postureImage}
            alt="Posture"
            style={{ width: "100%", maxHeight: "150px", borderRadius: "6px", objectFit: "contain" }}
          />
        </Box>

        {/* Size Section */}
        <Box sx={{ width: "100%" }}>
          <Typography fontWeight={500} fontSize={{ xs: "14px", sm: "16px" }} color="#3F3F3F" mb={0.5}>
            Size:
          </Typography>
          <img
            src={sizeImage}
            alt="Size"
            style={{ width: "100%", maxHeight: "170px", borderRadius: "6px", objectFit: "contain" }}
          />
        </Box>

        {/* Checkbox Agreement */}
        <Box display="flex" justifyContent="center" width="100%" sx={{ mt: 0.5 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={isChecked}
                onChange={(e) => {
                  setIsChecked(e.target.checked);
                  if (e.target.checked) setShowError(false);
                }}
                sx={{
                  color: "#007BFF",
                  "&.Mui-checked": {
                    color: "#007BFF",
                  },
                  padding: { xs: "4px", sm: "9px" },
                }}
              />
            }
            label={
              <Typography sx={{ fontSize: { xs: "12px", sm: "14px" }, color: "#3F3F3F" }}>
                By using the MBF MIS, you consent to the processing of your
                contact information by Maker Bhavan Foundation for marketing &
                communication purposes. Please confirm that you and read and
                understood the instructions above
              </Typography>
            }
            sx={{
              alignItems: "flex-start",
            }}
          />
        </Box>

        {/* Error Message */}
        {showError && (
          <Alert severity="error" sx={{ width: "100%" }}>
            Please confirm that you have read the instructions before
            proceeding.
          </Alert>
        )}

        {/* Buttons */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
            mt: { xs: 0.5, sm: 1 },
          }}
        >
          <Typography
            sx={{
              color: "#007BFF",
              fontWeight: 500,
              fontSize: { xs: "13px", sm: "14px" },
              cursor: "pointer",
              alignSelf: "center",
            }}
            onClick={() => navigate("/login")}
          >
            &lt; Back
          </Typography>

          <Button
            variant="contained"
            onClick={handleOpenCamera}
            sx={{
              backgroundColor: isChecked ? "#007BFF" : "#ccc",
              color: "#fff",
              fontWeight: 600,
              fontSize: { xs: "13px", sm: "14px" },
              padding: { xs: "8px 16px", sm: "10px 20px" },
              borderRadius: "10px",
              textTransform: "none",
              boxShadow: "none",
              cursor: isChecked ? "pointer" : "not-allowed",
              "&:hover": {
                backgroundColor: isChecked ? "#B2D7FF" : "#ccc",
                boxShadow: "none",
              },
            }}
          >
            Open Camera
          </Button>
        </Box>
      </PageTransition>

      {/* Powered by Section */}
      <Box
        sx={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "0.3rem",
          padding: { xs: "8px", sm: "12px" },
          backgroundColor: { xs: "#FFFFFF", sm: "transparent" },
        }}
      >
        <Typography fontSize={{ xs: "11px", sm: "12px" }} color="#666">
          Powered by
        </Typography>
        <img src={mapITlogo} alt="MapIT Logo" style={{ height: "30px" }} />
      </Box>
    </Box>
  );
};

export default RegisterInstructions;
